import { axiosIns } from "../utils";
import { normalizeBackendFileUrl, unwrapApiData } from "../utils/backendApi";

const toNumberIfPossible = (value) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  const next = Number(value);
  return Number.isNaN(next) ? value : next;
};

const toArray = (payload, key) => {
  const data = unwrapApiData(payload);

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.[key])) {
    return data[key];
  }

  return [];
};

const normalizeUser = (user) => {
  if (!user || typeof user !== "object") {
    return null;
  }

  return {
    ...user,
    id: toNumberIfPossible(user.id),
    first_name: user.first_name || user.profile?.first_name || "",
    last_name: user.last_name || user.profile?.last_name || "",
  };
};

const normalizeParagraph = (paragraph, fallbackArticleId = null) => ({
  ...paragraph,
  id: toNumberIfPossible(paragraph?.id),
  article: toNumberIfPossible(
    paragraph?.article ??
      paragraph?.articleId ??
      paragraph?.article_id ??
      fallbackArticleId
  ),
  subtitle: paragraph?.subtitle || "",
  content: paragraph?.content || "",
  paragraph_pic: normalizeBackendFileUrl(
    paragraph?.paragraph_pic || paragraph?.paragraphPic || null,
  ),
});

const normalizeArticle = (article) => {
  if (!article || typeof article !== "object") {
    return null;
  }

  const normalizedId = toNumberIfPossible(article.id);
  const normalizedCategoryId = toNumberIfPossible(
    article?.category_id ?? article?.categoryId ?? article?.category?.id ?? article?.category
  );
  const normalizedComments = Array.isArray(article?.comments) ? article.comments : [];
  const normalizedLikes =
    typeof article?.likes_count === "number"
      ? article.likes_count
      : Array.isArray(article?.likes)
        ? article.likes.length
        : 0;

  return {
    ...article,
    id: normalizedId,
    title: article?.title || "",
    category: normalizedCategoryId,
    category_id: normalizedCategoryId,
    category_name: article?.category_name || article?.category?.designation || "",
    article_pic: normalizeBackendFileUrl(
      article?.article_pic || article?.articlePic || null,
    ),
    publication_date:
      article?.publication_date || article?.publicationDate || null,
    author: normalizeUser(article?.author),
    validated_by: normalizeUser(article?.validated_by || article?.validatedBy),
    validated: Boolean(article?.validated),
    likes_count: normalizedLikes,
    comments: normalizedComments,
    paragraphs: Array.isArray(article?.paragraphs)
      ? article.paragraphs.map((paragraph) =>
          normalizeParagraph(paragraph, normalizedId)
        )
      : [],
  };
};

const normalizeCategory = (category) => ({
  ...category,
  id: toNumberIfPossible(category?.id),
  designation: category?.designation || "",
});

const buildArticleFormData = (payload = {}) => {
  const formData = new FormData();

  if (payload?.title !== undefined) {
    formData.append("title", payload.title || "");
  }

  if (payload?.category_id !== undefined && payload?.category_id !== null) {
    formData.append("category_id", String(payload.category_id));
  }

  if (payload?.article_pic instanceof File) {
    formData.append("article_pic", payload.article_pic);
  } else if (typeof payload?.article_pic === "string") {
    formData.append("article_pic", payload.article_pic);
  }

  const paragraphs = (Array.isArray(payload?.paragraphs) ? payload.paragraphs : []).map(
    (paragraph, index) => {
      if (paragraph?.paragraph_pic instanceof File) {
        formData.append(`paragraph_pic_${index}`, paragraph.paragraph_pic);
      }

      return {
        ...(paragraph?.id ? { id: String(paragraph.id) } : {}),
        subtitle: paragraph?.subtitle || "",
        content: paragraph?.content || "",
        paragraph_pic:
          paragraph?.paragraph_pic instanceof File
            ? null
            : paragraph?.paragraph_pic || null,
      };
    }
  );

  formData.append("paragraphs", JSON.stringify(paragraphs));

  return formData;
};

export const fetchArticles = async (params = {}) => {
  const query = {};

  if (params?.status) {
    query.status = params.status;
  }
  if (params?.search) {
    query.search = params.search;
  }
  if (params?.category) {
    query.category = params.category;
  }
  if (params?.start_date) {
    query.start_date = params.start_date;
  }
  if (params?.end_date) {
    query.end_date = params.end_date;
  }

  const response = await axiosIns.get("/articles/", {
    params: query,
  });
  return toArray(response?.data ?? response, "articles")
    .map(normalizeArticle)
    .filter(Boolean);
};

export const fetchArticleById = async (articleId) => {
  const response = await axiosIns.get(`/articles/${articleId}/`);
  return normalizeArticle(response?.data ?? response);
};

export const fetchArticleCategories = async () => {
  const response = await axiosIns.get("/categories/");
  return toArray(response?.data ?? response, "categories").map(normalizeCategory);
};

export const deleteArticleApi = async (articleId) => {
  const response = await axiosIns.delete(`/articles/${articleId}/`);
  return unwrapApiData(response?.data ?? response);
};

export const createArticleApi = async (payload) => {
  const response = await axiosIns.post("/articles/", buildArticleFormData(payload));
  return normalizeArticle(response?.data ?? response);
};

export const updateArticleApi = async (articleId, payload) => {
  const response = await axiosIns.patch(
    `/articles/${articleId}/`,
    buildArticleFormData(payload),
  );
  return normalizeArticle(response?.data ?? response);
};

export const createParagraphApi = async (payload) => {
  const response = await axiosIns.post("/paragraphs/", payload);
  return normalizeParagraph(response?.data ?? response, payload?.article);
};

export const updateParagraphApi = async (paragraphId, payload) => {
  const response = await axiosIns.patch(`/paragraphs/${paragraphId}/`, payload);
  return normalizeParagraph(response?.data ?? response, payload?.article);
};

export const deleteParagraphApi = async (paragraphId) => {
  const response = await axiosIns.delete(`/paragraphs/${paragraphId}/`);
  return unwrapApiData(response?.data ?? response);
};

export const validateArticleApi = async (articleId) => {
  const response = await axiosIns.patch(`/articles/${articleId}/validate/`);
  return normalizeArticle(response?.data ?? response);
};
